import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import Button, { IconButton } from '@atlaskit/button/new';
import ThumbsUpIcon from '@atlaskit/icon/core/thumbs-up';
import ThumbsDownIcon from '@atlaskit/icon/core/thumbs-down';
import { token } from '@atlaskit/tokens';
import { xcss, Flex, Box } from '@atlaskit/primitives';
import ClockIcon from '@atlaskit/icon/core/clock';
import Link from '@atlaskit/link';
import CopyIcon from '@atlaskit/icon/core/migration/copy';

import { Divider } from '@atlassian/navigation-system/side-nav-items/menu-section';

import { withErrorBoundary, Attribution } from '@confluence/error-boundary';

import { mockData } from '../../mockData';
import { messages } from '../../messages';

import { LoomScriptExpand } from './LoomScriptExpand';

const scriptContainerStyles = xcss({ paddingInline: 'space.300' });
const videoLink = 'https://www.youtube.com/watch?v=example'; // Replace with actual video link

const scriptTimeStyles = xcss({
	paddingBlock: 'space.100',
	paddingInline: 'space.050',
	color: 'color.text.subtlest',
	font: token('font.body.small'),
});

const loomAIGeneratedScriptStyles = xcss({
	paddingBlock: 'space.100',
	paddingInline: 'space.150',
	marginTop: 'space.100',
	marginBottom: 'space.100',
	whiteSpace: 'pre-wrap',
});

const recordVideoButtonContainerStyles = xcss({
	paddingBlock: 'space.200',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

const helpSectionStyles = xcss({
	padding: 'space.200',
	marginTop: 'space.200',
	marginBottom: 'space.300',
	backgroundColor: 'color.background.accent.gray.subtlest',
	borderRadius: 'border.radius.100',
});

const linkWrapperStyles = xcss({ marginTop: 'space.100' });

const iconWrapperStyles = xcss({
	marginTop: 'space.100',
	marginBottom: 'space.100',
});

const ScriptDuration = () => (
	<Flex alignItems="center" gap="space.025" wrap="wrap">
		<ClockIcon label="Video time" />
		<Box xcss={scriptTimeStyles}>
			<FormattedMessage {...messages.loomScriptTime} values={{ time: mockData.readTime }} />
		</Box>
	</Flex>
);

const LoomAIGeneratedScript = () => (
	<Box as="section" aria-labelledby="ai-script-heading">
		<Box xcss={loomAIGeneratedScriptStyles}>{mockData.loomAIGeneratedScript}</Box>
	</Box>
);

type RecordVideoButtonProps = {
	onRecordVideoClick?: () => void;
};

const RecordVideoButton = ({ onRecordVideoClick }: RecordVideoButtonProps) => (
	<Box xcss={recordVideoButtonContainerStyles} testId="loom-script-panel-record-video">
		<Button
			shouldFitContainer
			appearance="primary"
			testId="loom-script-panel-record-video-button"
			onClick={() => onRecordVideoClick?.()}
		>
			<FormattedMessage {...messages.recordVideoButton} />
		</Button>
	</Box>
);

const LoomScriptHelpSection = () => (
	<Box as="section" aria-labelledby="loom-script-help-heading" xcss={helpSectionStyles}>
		<FormattedMessage {...messages.loomScriptNeedHelpSectionTitle} />
		<Box xcss={linkWrapperStyles}>
			<Link href={videoLink} target="_blank">
				<FormattedMessage {...messages.loomScriptWatchDemo} />
			</Link>
		</Box>
	</Box>
);

const CopyButton = () => {
	const [copied, setCopied] = useState(false);
	const [error, setError] = useState(false);

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(mockData.loomAIGeneratedScript);
			setCopied(true);
			setError(false);
		} catch {
			setCopied(false);
			setError(true);
		}
	};

	useEffect(() => {
		if (copied || error) {
			const timer = setTimeout(() => {
				setCopied(false);
				setError(false);
			}, 2000);
			return () => clearTimeout(timer);
		}
	}, [copied, error]);

	return (
		<Box xcss={iconWrapperStyles} testId="loom-script-copy-button">
			<Button iconBefore={CopyIcon} appearance="subtle" onClick={handleCopy}>
				{copied ? (
					<FormattedMessage {...messages.loomScriptCopied} />
				) : error ? (
					<FormattedMessage {...messages.loomScriptCopyFailed} />
				) : (
					<FormattedMessage {...messages.loomScriptCopy} />
				)}
			</Button>
		</Box>
	);
};

const FeedbackGroup = () => {
	const [thumbs, setThumbs] = useState<'up' | 'down' | undefined>(undefined);
	return (
		<Flex alignItems="center" gap="space.050" wrap="nowrap">
			<IconButton
				appearance="subtle"
				icon={ThumbsUpIcon}
				label="Thumbs Up"
				testId="import-feedback-banner-thumbs-up-button"
				onClick={() => {
					if (thumbs === 'up') {
						setThumbs(undefined); // Toggle off if already selected
					} else {
						setThumbs('up');
						// Handle thumbs up click such as submitting feedback (analytics event, etc.)
					}
				}}
				isSelected={thumbs === 'up'}
				aria-pressed={thumbs === 'up' ? 'true' : 'false'}
			/>
			<IconButton
				appearance="subtle"
				icon={ThumbsDownIcon}
				label="Thumbs Down"
				testId="import-feedback-banner-thumbs-down-button"
				onClick={() => {
					if (thumbs === 'down') {
						setThumbs(undefined); // Toggle off if already selected
					} else {
						setThumbs('down');
						// Handle thumbs down click such as submitting feedback (analytics event, etc.)
					}
				}}
				isSelected={thumbs === 'down'}
				aria-pressed={thumbs === 'down' ? 'true' : 'false'}
			/>
		</Flex>
	);
};

type LoomScriptPanelBodyComponentProps = {
	onRecordVideoClick?: () => void;
};

const LoomScriptPanelBodyComponent = ({
	onRecordVideoClick,
}: LoomScriptPanelBodyComponentProps) => {
	const intl = useIntl();
	return (
		<Box xcss={scriptContainerStyles} testId="loom-script-panel-body">
			<ScriptDuration />
			<LoomScriptExpand title={intl.formatMessage(messages.loomScriptExpandSectionTitle)}>
				{mockData.videoPurposeText}
			</LoomScriptExpand>
			<LoomAIGeneratedScript />
			<Flex>
				<CopyButton />
				<FeedbackGroup />
			</Flex>
			<LoomScriptHelpSection />
			<Divider />
			<RecordVideoButton onRecordVideoClick={onRecordVideoClick} />
		</Box>
	);
};

export const LoomScriptPanelBody = withErrorBoundary({
	attribution: Attribution.GROWTH_MULTIPLIERS,
})(LoomScriptPanelBodyComponent);
